import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { 
  ArrowLeft, Clock, CheckCircle2, AlertCircle, MessageSquare, 
  User, Shield, Trash2, Edit2, MoreVertical, Send, Plus, Bookmark, BookmarkCheck
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  answered: { label: 'Pending Verification', color: 'bg-amber-100 text-amber-700', icon: MessageSquare },
  verified: { label: 'Verified', color: 'bg-green-100 text-green-700', icon: CheckCircle2 }
};

const ForumQuestionPage = () => {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  // Check if coming from admin page
  const fromAdmin = searchParams.get('from') === 'admin';
  
  const [question, setQuestion] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);
  
  const [newAnswer, setNewAnswer] = useState('');
  const [newFollowUp, setNewFollowUp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);
  
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null });

  const isAdmin = user?.role === 'admin' || user?.role === 'sub_admin';
  
  // Determine back navigation
  const handleBack = () => {
    if (fromAdmin && isAdmin) {
      navigate('/admin/forum');
    } else {
      navigate('/forum');
    }
  };
  
  const backButtonText = fromAdmin && isAdmin ? 'Back to Forum Management' : 'Back to Forum';
  const isVerified = question?.status === 'verified';

  const fetchQuestion = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/forum/questions/${questionId}`);
      setQuestion(response.data.question);
      setAnswers(response.data.answers);
      setFollowUps(response.data.follow_ups);
      
      // Check bookmark status
      try {
        const bookmarkRes = await axios.get(`${API}/forum/bookmarks/check/${questionId}`);
        setIsBookmarked(bookmarkRes.data.bookmarked);
      } catch (e) {
        // Silently ignore if bookmark check fails
      }
    } catch (error) {
      console.error('Failed to fetch question:', error);
      if (error.response?.status === 404) {
        toast.error('Question not found');
        navigate('/forum');
      } else if (error.response?.status === 403) {
        toast.error('Forum access requires a paid subscription');
        navigate('/forum');
      }
    } finally {
      setLoading(false);
    }
  }, [questionId, navigate]);

  const handleToggleBookmark = async () => {
    try {
      setBookmarkLoading(true);
      if (isBookmarked) {
        await axios.delete(`${API}/forum/bookmarks/${questionId}`);
        setIsBookmarked(false);
        toast.success('Bookmark removed');
      } else {
        await axios.post(`${API}/forum/bookmarks/${questionId}`);
        setIsBookmarked(true);
        toast.success('Question bookmarked');
      }
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
      toast.error('Failed to update bookmark');
    } finally {
      setBookmarkLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestion();
  }, [fetchQuestion]);

  const handleSubmitAnswer = async () => {
    if (!newAnswer.trim() || newAnswer.length < 10) {
      toast.error('Answer must be at least 10 characters');
      return;
    }
    
    try {
      setSubmitting(true);
      await axios.post(`${API}/forum/questions/${questionId}/answers`, {
        content: newAnswer
      });
      toast.success('Answer submitted successfully');
      setNewAnswer('');
      fetchQuestion();
    } catch (error) {
      console.error('Failed to submit answer:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitFollowUp = async () => {
    if (!newFollowUp.trim() || newFollowUp.length < 10) {
      toast.error('Follow-up must be at least 10 characters');
      return;
    }
    
    try {
      setSubmitting(true);
      await axios.post(`${API}/forum/questions/${questionId}/follow-ups`, {
        content: newFollowUp
      });
      toast.success('Follow-up added');
      setNewFollowUp('');
      setShowFollowUpForm(false);
      fetchQuestion();
    } catch (error) {
      console.error('Failed to submit follow-up:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit follow-up');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyAnswer = async (answerId) => {
    try {
      await axios.post(`${API}/forum/answers/${answerId}/verify`);
      toast.success('Answer verified');
      fetchQuestion();
    } catch (error) {
      console.error('Failed to verify answer:', error);
      toast.error('Failed to verify answer');
    }
  };

  const handleDelete = async () => {
    const { type, id } = deleteDialog;
    try {
      if (type === 'question') {
        await axios.delete(`${API}/forum/questions/${id}`);
        toast.success('Question deleted');
        navigate('/forum');
      } else if (type === 'answer') {
        await axios.delete(`${API}/forum/answers/${id}`);
        toast.success('Answer deleted');
        fetchQuestion();
      } else if (type === 'followup') {
        await axios.delete(`${API}/forum/follow-ups/${id}`);
        toast.success('Follow-up deleted');
        fetchQuestion();
      }
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete');
    } finally {
      setDeleteDialog({ open: false, type: null, id: null });
    }
  };

  const handleReopen = async () => {
    try {
      await axios.post(`${API}/forum/questions/${questionId}/reopen`);
      toast.success('Question reopened');
      fetchQuestion();
    } catch (error) {
      console.error('Failed to reopen:', error);
      toast.error('Failed to reopen question');
    }
  };

  const canDelete = (item) => {
    return item.user_id === user?.id || isAdmin;
  };

  const canEdit = (item) => {
    if (isVerified && !isAdmin) return false;
    return item.user_id === user?.id || isAdmin;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  if (!question) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-600">Question not found</p>
        </div>
      </Layout>
    );
  }

  const statusConfig = STATUS_CONFIG[question.status];
  const StatusIcon = statusConfig.icon;

  return (
    <Layout>
      <div className="page-transition max-w-4xl mx-auto">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {backButtonText}
        </Button>

        {/* Question Card */}
        <Card className="mb-6" data-testid="question-detail">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Badge className={statusConfig.color}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {statusConfig.label}
                  </Badge>
                  {question.function_name && (
                    <Badge variant="outline" className="max-w-full whitespace-normal break-words text-left">{question.function_name}</Badge>
                  )}
                  {question.topic_name && (
                    <Badge variant="outline" className="bg-gray-50 max-w-full whitespace-normal break-words text-left">{question.topic_name}</Badge>
                  )}
                  {question.mmd_location && (
                    <Badge variant="outline" className="bg-purple-50 text-purple-700">
                      {question.mmd_location}
                    </Badge>
                  )}
                </div>
                <CardTitle className="text-xl mb-2">{question.title}</CardTitle>
              </div>
              
              <div className="flex items-center gap-1">
                {/* Bookmark button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleToggleBookmark}
                  disabled={bookmarkLoading}
                  className={isBookmarked ? 'text-amber-500 hover:text-amber-600' : 'text-gray-400 hover:text-amber-500'}
                  data-testid="bookmark-btn"
                >
                  {isBookmarked ? (
                    <BookmarkCheck className="w-5 h-5" />
                  ) : (
                    <Bookmark className="w-5 h-5" />
                  )}
                </Button>
                
                {(canEdit(question) || isAdmin) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canEdit(question) && (
                      <DropdownMenuItem onClick={() => navigate(`/forum/edit/${question.id}`)}>
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                    )}
                    {isAdmin && isVerified && (
                      <DropdownMenuItem onClick={handleReopen}>
                        <AlertCircle className="w-4 h-4 mr-2" />
                        Reopen
                      </DropdownMenuItem>
                    )}
                    {canDelete(question) && (
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={() => setDeleteDialog({ open: true, type: 'question', id: question.id })}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none mb-4">
              <p className="whitespace-pre-wrap">{question.content}</p>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                {question.user_role !== 'user' ? (
                  <Shield className="w-4 h-4 text-amber-500" />
                ) : (
                  <User className="w-4 h-4" />
                )}
                <span>{question.user_name}</span>
                {question.user_role !== 'user' && (
                  <Badge className="bg-amber-100 text-amber-700 text-xs">
                    {question.user_role === 'admin' ? 'Admin' : 'Moderator'}
                  </Badge>
                )}
              </div>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {format(new Date(question.created_at), 'PPp')}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Follow-ups Section */}
        {followUps.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Follow-up Questions ({followUps.length})
            </h3>
            <div className="space-y-3">
              {followUps.map((followUp) => (
                <Card key={followUp.id} className="bg-blue-50/50 border-blue-100">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-sm whitespace-pre-wrap">{followUp.content}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                          <span>{followUp.user_name}</span>
                          {followUp.user_role !== 'user' && (
                            <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                              {followUp.user_role === 'admin' ? 'Admin' : 'Mod'}
                            </Badge>
                          )}
                          <span>{formatDistanceToNow(new Date(followUp.created_at), { addSuffix: true })}</span>
                        </div>
                      </div>
                      {canDelete(followUp) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteDialog({ open: true, type: 'followup', id: followUp.id })}
                        >
                          <Trash2 className="w-3 h-3 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Add Follow-up (only if not verified) */}
        {!isVerified && (
          <div className="mb-6">
            {showFollowUpForm ? (
              <Card>
                <CardContent className="p-4">
                  <Textarea
                    placeholder="Add a follow-up question or clarification..."
                    value={newFollowUp}
                    onChange={(e) => setNewFollowUp(e.target.value)}
                    rows={3}
                    className="mb-3"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSubmitFollowUp}
                      disabled={submitting}
                      size="sm"
                    >
                      {submitting ? 'Adding...' : 'Add Follow-up'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowFollowUpForm(false);
                        setNewFollowUp('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFollowUpForm(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Follow-up Question
              </Button>
            )}
          </div>
        )}

        <Separator className="my-6" />

        {/* Answers Section */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Answers ({answers.length})
          </h3>
          
          {answers.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No answers yet. Be the first to help!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {answers.map((answer) => (
                <Card 
                  key={answer.id}
                  className={answer.is_verified ? 'border-green-200 bg-green-50/30' : ''}
                  data-testid={`answer-${answer.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        {answer.is_verified && (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Verified Answer
                          </Badge>
                        )}
                        {answer.user_role !== 'user' && (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Shield className="w-3 h-3 mr-1" />
                            {answer.user_role === 'admin' ? 'Admin' : 'Moderator'}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {isAdmin && !answer.is_verified && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleVerifyAnswer(answer.id)}
                            className="text-green-600 border-green-300 hover:bg-green-50"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Verify
                          </Button>
                        )}
                        
                        {canDelete(answer) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {canEdit(answer) && (
                                <DropdownMenuItem>
                                  <Edit2 className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={() => setDeleteDialog({ open: true, type: 'answer', id: answer.id })}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                    
                    <div className="prose prose-sm max-w-none mb-3">
                      <p className="whitespace-pre-wrap">{answer.content}</p>
                    </div>
                    
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>{answer.user_name}</span>
                      <span>{format(new Date(answer.created_at), 'PPp')}</span>
                      {answer.is_verified && answer.verified_by_name && (
                        <span className="text-green-600">
                          Verified by {answer.verified_by_name}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Add Answer Form (only if not verified) */}
        {!isVerified ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Your Answer</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Share your knowledge and help answer this question..."
                value={newAnswer}
                onChange={(e) => setNewAnswer(e.target.value)}
                rows={5}
                className="mb-4"
                data-testid="answer-input"
              />
              <Button
                onClick={handleSubmitAnswer}
                disabled={submitting || !newAnswer.trim()}
                className="bg-[#0A7EA4]"
                data-testid="submit-answer-btn"
              >
                {submitting ? (
                  'Submitting...'
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Answer
                  </>
                )}
              </Button>
              {isAdmin && (
                <p className="text-xs text-gray-500 mt-2">
                  As an admin, your answer will be automatically verified.
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-green-50">
            <CardContent className="py-6 text-center">
              <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
              <p className="text-green-700 font-medium">This question has been verified</p>
              <p className="text-sm text-green-600">No new answers or follow-ups can be added.</p>
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, type: null, id: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the {deleteDialog.type}.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default ForumQuestionPage;
